import { faFilter } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { JSX } from 'react';
import { useEffect, useState, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import { Color } from '@/constants/color';
import AppContext from '@/contexts/appContext';
import Avatar from '@/ui/components/Avatar';
import Badge from '@/ui/components/Badge';
import Button from '@/ui/components/Button';
import Card from '@/ui/components/Card';
import Container from '@/ui/components/Container';
import Paginator from '@/ui/components/Paginator';

import { billingReportResultRepository } from './repositories/BillingReportResultRepository';
import { BillingReport, BillingReportResult } from './types';

function billingReportResultStatusColor(status: string): Color {
  switch (status) {
    case 'success':
      return Color.GREEN;
    case 'processing':
      return Color.BLUE;
    case 'failure':
      return Color.RED;
    default:
      return Color.GRAY;
  }
}

function formatDuration(startedAt: string | null, completedAt: string | null): string {
  if (!startedAt || !completedAt) return '';

  const start = new Date(startedAt);
  const end = new Date(completedAt);
  const durationMs = end.getTime() - start.getTime();

  const seconds = Math.floor(durationMs / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);

  if (hours > 0) {
    return `${hours}h ${minutes % 60}m`;
  } else if (minutes > 0) {
    return `${minutes}m ${seconds % 60}s`;
  } else {
    return `${seconds}s`;
  }
}

function calculateRPS(result: BillingReportResult): string {
  if (!result.processed_started_at || !result.processed_completed_at || result.row_count === 0) {
    return '';
  }

  const start = new Date(result.processed_started_at);
  const end = new Date(result.processed_completed_at);
  const durationSeconds = (end.getTime() - start.getTime()) / 1000;

  if (durationSeconds === 0) return '';

  const rps = result.row_count / durationSeconds;
  return rps.toFixed(2);
}

const BillingReportResultsIndex = (): JSX.Element => {
  const { billingReportId } = useParams<{ billingReportId: string }>();
  const { host } = useContext(AppContext);
  const navigate = useNavigate();
  const [results, setResults] = useState<BillingReportResult[]>([]);
  const [billingReport, setBillingReport] = useState<BillingReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [perPage] = useState<number>(5);

  useEffect(() => {
    if (!billingReportId) return;
    billingReportResultRepository
      .getBillingReport({ host, billingReportId })
      .then(setBillingReport)
      .catch((e) => console.error('Failed to load billing report:', e));
  }, [host, billingReportId]);

  useEffect(() => {
    if (!billingReportId) return;
    setLoading(true);
    billingReportResultRepository
      .listByBillingReport({ host, billingReportId, page, perPage })
      .then(({ results: data, total_pages, page: currentPage }) => {
        setResults(data);
        setTotalPages(total_pages);
        setPage(currentPage);
      })
      .catch((e) => console.error('Failed to load billing report results:', e))
      .finally(() => setLoading(false));
  }, [host, billingReportId, page, perPage]);

  return (
    <Container type="full-width-page">
      <div className="flex items-center justify-between mb-4">
        <div>
          <Button onClick={() => navigate('/billing/reports')} color="white" className="mb-2">
            Back to Reports
          </Button>
          <h1 className="text-2xl font-semibold">
            {billingReport?.name || 'Billing Report Results'}
          </h1>
        </div>
        <div>
          {billingReportId && (
            <Button
              color="blue"
              onClick={() => navigate(`/billing/reports/${billingReportId}/edit`)}>
              <FontAwesomeIcon icon={faFilter} className="mr-2" />
              Edit Report Filter
            </Button>
          )}
        </div>
      </div>

      {loading ? (
        <div>Loading...</div>
      ) : results.length === 0 ? (
        <Card className="p-4">No results found for this billing report.</Card>
      ) : (
        <Card className="flex flex-col">
          <div className="overflow-x-auto">
            <table className="table-auto w-full border-collapse text-left text-sm">
              <thead>
                <tr className="border-b">
                  <th className="px-4 py-3">Processed By</th>
                  <th className="px-4 py-3">Type</th>
                  <th className="px-4 py-3">Process Started At</th>
                  <th className="px-4 py-3">Process Completed At</th>
                  <th className="px-4 py-3">Duration</th>
                  <th className="px-4 py-3">RPS</th>
                  <th className="px-4 py-3">Rows</th>
                  <th className="px-4 py-3">Errors</th>
                  <th className="px-4 py-3">Error Rate</th>
                  <th className="px-4 py-3">Submitted</th>
                  <th className="px-4 py-3">Submitted Rate</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {results.map((result) => (
                  <tr key={result.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-4 py-3">
                      <Avatar
                        avatarUrl={result.processed_by?.avatar_url || undefined}
                        name={
                          [result.processed_by?.first_name, result.processed_by?.last_name]
                            .filter(Boolean)
                            .join(' ') || 'Unknown'
                        }
                      />
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Badge
                          text={result.status}
                          color={billingReportResultStatusColor(result.status)}
                        />
                        <span className="capitalize">{result.type}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      {result.processed_started_at
                        ? new Date(result.processed_started_at).toLocaleString()
                        : ''}
                    </td>
                    <td className="px-4 py-3">
                      {result.processed_completed_at
                        ? new Date(result.processed_completed_at).toLocaleString()
                        : ''}
                    </td>
                    <td className="px-4 py-3">
                      {formatDuration(result.processed_started_at, result.processed_completed_at)}
                    </td>
                    <td className="px-4 py-3">{calculateRPS(result)}</td>
                    {result.processed_completed_at ? (
                      <>
                        <td className="px-4 py-3">{result.row_count.toLocaleString()}</td>
                        <td className="px-4 py-3">{result.error_count.toLocaleString()}</td>
                        <td className="px-4 py-3">{(result.error_rate * 100).toFixed(1)}%</td>
                        <td className="px-4 py-3">{result.submitted_count.toLocaleString()}</td>
                        <td className="px-4 py-3">{(result.submitted_rate * 100).toFixed(1)}%</td>
                      </>
                    ) : (
                      <td colSpan={5} className="px-4 py-3 text-gray-400 text-center">
                        Processing...
                      </td>
                    )}
                    <td className="px-4 py-3 whitespace-nowrap text-right">
                      <Button
                        onClick={() =>
                          navigate(`/billing/reports/${billingReportId}/results/${result.id}/`)
                        }
                        color="blue"
                        size="sm">
                        View
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-4 px-4 pb-4">
            <Paginator
              currentPage={page}
              totalPages={totalPages}
              goToPage={setPage}
              nextPage={() => setPage((p) => Math.min(totalPages, p + 1))}
              prevPage={() => setPage((p) => Math.max(1, p - 1))}
            />
          </div>
        </Card>
      )}
    </Container>
  );
};

export default BillingReportResultsIndex;
